<template>
    <component
        :is="tag"
        :draggable="draggable"
        :disabled="disabled"
        @drag="emitDragEvent(events.drag, $event)"
        @dragstart="emitDragEvent(events.dragstart, $event)"
        @dragenter="emitDragEvent(events.dragenter, $event)"
        @dragleave="emitDragEvent(events.dragleave, $event)"
        @dragend="emitDragEvent(events.dragend, $event)"
        @mouseover="emitMouseEvent('mouseover', $event)"
        @mouseout="emitMouseEvent('mouseout', $event)"
        @click="emitMouseEvent('click', $event)"
    >
        <slot :transfer-data="scopedData"></slot>
        <div v-if="hideImageHtml" :style="hideImageStyle">
            <slot name="image" :transfer-data="scopedData"></slot>
        </div>
        <slot v-else name="image" :transfer-data="scopedData"></slot>
    </component>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop } from '@/helpers/Decorators';
import { dropEffects, effectsAllowed, events } from './constants';
import transferDataStore from './transferDataStore';

@Options({
    name: 'vue-drag',
    emits: [
        events.drag,
        events.dragstart,
        events.dragenter,
        events.dragleave,
        events.dragend,
        'mouseover',
        'mouseout',
        'click'
    ]
})
export default class Drag extends Vue
{
    @Prop({ default: true })
    public draggable: boolean;

    @Prop()
    public transferData: any;

    @Prop({ validator: (value: string) => value in dropEffects })
    public dropEffect: string;

    @Prop({ validator: (value: string) => value in effectsAllowed })
    public effectAllowed: string;

    @Prop()
    public image: string;

    @Prop({ default: 0 })
    public imageXOffset: number;

    @Prop({ default: 0 })
    public imageYOffset: number;

    @Prop({ default: true })
    public hideImageHtml: boolean;

    @Prop({ default: 'div' })
    public tag: string;

    @Prop({ default: false })
    public disabled: boolean;

    @Prop({ default: false })
    public autoScroll: boolean;

    public dragging: boolean = false;
    public scrollInterval: NodeJS.Timer | null = null;
    public scrollParent: HTMLElement | null = null;
    public lastMouseY: number = 0;

    public get events(): Record<string, string>
    {
        return events;
    }

    public get scopedData(): boolean
    {
        return this.dragging && this.transferData;
    }

    public get hideImageStyle(): any
    {
        return { position: 'fixed', top: '-1000px' };
    }

    public emitMouseEvent(name: string, nativeEvent: any): void
    {
        if (this.disabled) return;

        nativeEvent.stopPropagation();

        this.$emit(name, nativeEvent);
    }

    public findScrollParent(element: HTMLElement)
    {
        let style = getComputedStyle(element);
        const excludeStaticParent = style.position === "absolute";
        const overflowRegex = /(auto|scroll)/;

        if (style.position === "fixed") return document.body;

        for (let parent = element; (parent = parent.parentElement);)
        {
            style = getComputedStyle(parent);

            if (excludeStaticParent && style.position === "static")
            {
                continue;
            }

            if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX))
            {
                return parent;
            }
        }

        return document.body;
    }

    public emitDragEvent(name: string, nativeEvent: any): void
    {
        nativeEvent.stopPropagation();

        const transfer = nativeEvent.dataTransfer;

        // Set drop effect on dragenter and dragover
        if ([events.dragenter, events.dragover].includes(name))
        {
            if (this.dropEffect)
            {
                transfer.dropEffect = this.dropEffect;
            }
        }

        // A number of things need to happen on drag start
        if (name === events.dragstart)
        {
            // Set the allowed effects
            if (this.effectAllowed)
            {
                transfer.effectAllowed = this.effectAllowed;
            }

            // Set the drag image
            if (this.image || this.$slots.image)
            {
                let image;

                if (this.image)
                {
                    image = new Image();
                    image.src = this.image;
                }
                else if (this.$slots.image)
                {
                    image = this.$slots.image[0].elm;
                }

                if (transfer.setDragImage)
                {
                    transfer.setDragImage(image, this.imageXOffset, this.imageYOffset);
                }
            }

            // Set the transfer data
            if (this.transferData !== undefined)
            {
                transferDataStore.data = this.transferData;
                // Set a dummy string for the real transfer data. Not actually used
                // for anything, but necesssary for browser compatibility.
                //
                // Maybe this should be the actual data serialized. But since
                // it's not actually used for anything it seems like a waste of CPU.
                nativeEvent.dataTransfer.setData('text', '');
            }

            // Auto scroll
            if (this.autoScroll)
            {
                this.scrollParent = this.findScrollParent(nativeEvent.target);

                if (this.scrollInterval)
                {
                    clearInterval(this.scrollInterval);
                }

                this.scrollInterval = setInterval(() =>
                {
                    const scrollY = this.scrollParent.scrollTop;
                    const viewportHeight = this.scrollParent.clientHeight;
                    const scrollAreaHeight = Math.min(viewportHeight / 4, 100);
                    const mouseY = this.lastMouseY - this.scrollParent.getBoundingClientRect().top;

                    if (mouseY < scrollAreaHeight) this.scrollParent.scrollTo(0, scrollY - 30);
                    else if (mouseY > viewportHeight - scrollAreaHeight) this.scrollParent.scrollTo(0, scrollY + 30);
                }, 100);
            }

            // Indicate that we're dragging.
            this.dragging = true;
        }

        if (name === events.drag && this.autoScroll)
        {
            this.lastMouseY = nativeEvent.clientY;
        }

        // At last, emit the event.
        this.$emit(name, this.transferData, nativeEvent);

        // Clean up stored data on drag end after emitting.
        if (name === events.dragend)
        {
            transferDataStore.data = undefined;

            if (this.autoScroll) clearInterval(this.scrollInterval);

            this.dragging = false;
        }
    }
}
</script>
